import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api, query } from '../../../api/client'
import { DEFAULT_FILTERS, filterParams, type CrashFilters, type CrashOverview } from '../../../api/crashes'
import { useDebounced } from '../../../hooks/useDebounced'
import { formatRelative } from '../../../lib/format'
import { Stat, StatCard } from '../../status/StatCard'
import { CollectorPausesPanel } from './CollectorPausesPanel'
import { CountList } from './CountList'
import { CrashFiltersBar } from './CrashFiltersBar'
import { useCrashAction } from './useCrashAction'

/** Admin → Crashes and Errors: StoreKeeper's crash and hang reports in a window, grouped by stack. */
export function CrashesPanel() {
	const [filters, setFilters] = useState<CrashFilters>(DEFAULT_FILTERS)
	const [page, setPage] = useState(1)
	const [selected, setSelected] = useState<Set<string>>(new Set())
	const applied = useDebounced(filters, 300)
	const overview = useQuery({
		queryKey: ['admin-crashes', applied, page],
		queryFn: () => api.get<CrashOverview>(`/api/v1/admin/crashes${query({ ...filterParams(applied), page })}`),
	})
	const resolve = useCrashAction((signatures: string[]) =>
		api.post<{ count: number; queued: number }>('/api/v1/admin/crashes/resolve', {
			signatures,
			reopen: applied.resolved,
			filter: {
				days: applied.days,
				build: applied.build || undefined,
				kind: applied.kind || undefined,
				shippingOnly: applied.shippingOnly,
			},
		}),
	)
	const remove = useCrashAction((signature: string) =>
		api.delete(`/api/v1/admin/crashes/groups/${signature}${query(filterParams(applied))}`),
	)
	const change = (next: CrashFilters) => {
		setFilters(next)
		setPage(1)
		setSelected(new Set())
	}
	const toggle = (signature: string) =>
		setSelected((s) => {
			const next = new Set(s)
			if (next.has(signature)) next.delete(signature)
			else next.add(signature)
			return next
		})
	const data = overview.data
	const link = (signature: string) => `/admin/crashes/group/${signature}${query(filterParams(applied))}`

	return (
		<>
			<CollectorPausesPanel />
			<section className="card stack">
				<div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
					<CrashFiltersBar value={filters} onChange={change} />
					<Link to="/admin/crashes/symbols" className="small">
						Symbols
					</Link>
				</div>
				<p className="muted small" style={{ margin: 0 }}>
					Counts are submitted diagnostics, not crash-free users. A crash delivered by both the crash extension and
					MetricKit counts once. Chart days are UTC.
				</p>
			</section>
			{overview.isError && <p className="error">{overview.error.message}</p>}
			{data && (
				<>
					<div className="grid">
						<StatCard title="Reports">
							<Stat label="Last 7 days" value={data.stats.last7Days} />
							<Stat label="Last 30 days" value={data.stats.last30Days} />
							<Stat label="Distinct installs in window" value={data.stats.installs} />
							<Stat
								label="Symbolicated in window"
								value={data.stats.total ? `${((100 * data.stats.symbolicated) / data.stats.total).toFixed(1)}%` : '—'}
							/>
						</StatCard>
						<StatCard title="By build and OS">
							<div className="row" style={{ alignItems: 'flex-start', gap: '2rem' }}>
								<CountList title="Build" counts={data.byBuild} />
								<CountList title="OS" counts={data.byOs} />
							</div>
						</StatCard>
					</div>
					<StatCard title="Reports per day">
						<ResponsiveContainer width="100%" height={180}>
							<BarChart data={data.daily}>
								<CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
								<XAxis dataKey="day" stroke="var(--text-3)" fontSize={12} minTickGap={24} />
								<YAxis stroke="var(--text-3)" fontSize={12} width={36} allowDecimals={false} />
								<Tooltip
									contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8 }}
								/>
								<Bar dataKey="crashes" name="Crashes" stackId="a" fill="var(--accent)" />
								<Bar dataKey="hangs" name="Hangs" stackId="a" fill="var(--danger)" />
							</BarChart>
						</ResponsiveContainer>
					</StatCard>
					<section className="card stack">
						<div className="row" style={{ justifyContent: 'space-between' }}>
							<h2 style={{ margin: 0 }}>Stack groups</h2>
							<button
								type="button"
								disabled={!selected.size || resolve.isPending}
								onClick={() => resolve.mutate([...selected], { onSuccess: () => setSelected(new Set()) })}
							>
								{applied.resolved ? 'Reopen selected' : 'Mark selected resolved'}
							</button>
						</div>
						{resolve.data && !applied.resolved && (
							<p className="small muted" role="status">
								Marked {resolve.data.count} resolved; {resolve.data.queued} sent back to the symbolizer.
							</p>
						)}
						<div className="table-scroll">
							<table>
								<thead>
									<tr>
										<th />
										<th>Stack</th>
										<th className="num">Reports</th>
										<th className="num">Installs</th>
										<th>Last received</th>
										<th>Builds</th>
										<th />
									</tr>
								</thead>
								<tbody>
									{data.groups.map((g) => (
										<tr key={g.signature}>
											<td>
												<input
													type="checkbox"
													aria-label={`Select ${g.title ?? g.signature}`}
													checked={selected.has(g.signature)}
													onChange={() => toggle(g.signature)}
												/>
											</td>
											<td>
												<Link to={link(g.signature)} className={g.title ? 'mono' : 'mono muted'}>
													{g.title ?? g.signature}
												</Link>
												<div className="muted small">
													{g.kinds.join(', ')} · {g.distributions.join(', ')}
												</div>
											</td>
											<td className="num">{g.count}</td>
											<td className="num">{g.installs}</td>
											<td className="muted small">{formatRelative(g.lastReceived)}</td>
											<td className="small">{g.builds.join(', ')}</td>
											<td>
												<button
													type="button"
													className="danger"
													onClick={() =>
														window.confirm(
															`Delete the ${g.count} reports in this group for this window? There is no undo.`,
														) && remove.mutate(g.signature)
													}
												>
													Delete
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
						{!data.groups.length && (
							<p className="muted">No {applied.resolved ? 'resolved' : 'open'} reports in this window.</p>
						)}
						<div className="row">
							{page > 1 && (
								<button type="button" onClick={() => setPage(page - 1)}>
									Previous
								</button>
							)}
							{data.hasMore && (
								<button type="button" onClick={() => setPage(page + 1)}>
									More
								</button>
							)}
						</div>
					</section>
				</>
			)}
		</>
	)
}
